import React, { useState, useRef, useEffect } from "react";
import { useGetInstitutionsQuery, useAddInstitutionMutation, useModifyInstitutionMutation, useDeleteInstitutionMutation } from "../../services/institutionsApi";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { Toolbar } from "primereact/toolbar";
import { Toast } from "primereact/toast";
import { AutoComplete } from "primereact/autocomplete";
import { useLazySearchInstitutionsQuery } from "../../services/institutionsApi";


export default function InstitutionManagementPage() {
  const { data: institutions, isLoading, isFetching } = useGetInstitutionsQuery();
  const [addInstitution] = useAddInstitutionMutation();
  const [modifyInstitution] = useModifyInstitutionMutation();
  const [deleteInstitution] = useDeleteInstitutionMutation();
  const [searchInstitutions] = useLazySearchInstitutionsQuery();
  const toast = useRef(null);

  const [newInstitution, setNewInstitution] = useState({ name: "", description: ""});
  const [showDialog, setShowDialog] = useState(false);
  const [lookupValue, setLookupValue] = useState("");
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [visibleRows, setVisibleRows] = useState(10);
  const tableWrapperRef = useRef(null);

  useEffect(() => {
    const calculateVisibleRows = () => {
      if (!tableWrapperRef.current) {
        return;
      }

      const tableTop = tableWrapperRef.current.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      const bottomSpacing = 32;
      const paginatorHeight = 56;
      const tableHeaderHeight = 48;
      const estimatedRowHeight = 46;
      const rowReductionFactor = 0.7;

      const availableHeight = viewportHeight - tableTop - bottomSpacing - paginatorHeight - tableHeaderHeight;
      const nextRows = Math.max(3, Math.floor((availableHeight / estimatedRowHeight) * rowReductionFactor));

      setVisibleRows((prevRows) => (prevRows === nextRows ? prevRows : nextRows));
    };

    calculateVisibleRows();
    window.addEventListener("resize", calculateVisibleRows);

    return () => {
      window.removeEventListener("resize", calculateVisibleRows);
    };
  }, []);

  

  const handleAddInstitution = async () => {
    try {
      setNewInstitution({ ...newInstitution })
      await addInstitution(newInstitution);
      setNewInstitution({ name: "", description: "" });
      setShowDialog(false);
      toast.current.show({ severity: 'success', summary: 'Sukces', detail: 'Instytucja dodana' });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się dodać instytucji' });
    }
  };

  const searchInstitutionSuggestions = async (event) => {
    const query = String(event.query || '').trim();
    if (!query) {
      setInstitutionSuggestions([]);
      return;
    }

    try {
      const items = await searchInstitutions({ q: query, source: 'all', limit: 20 }).unwrap();
      setInstitutionSuggestions(items);
    } catch (error) {
      setInstitutionSuggestions([]);
    }
  };

  const institutionSuggestionTemplate = (item) => (
    <div className="flex flex-column">
      <div className="flex justify-content-between gap-3 align-items-center">
        <span>{item.name}</span>
        <small className="text-color-secondary">{item.source === 'local' ? 'lokalna' : item.source}</small>
      </div>
      {item.description ? <small className="text-color-secondary">{item.description}</small> : null}
    </div>
  );

  const handleSuggestionSelect = (event) => {
    const selected = event.value;
    if (!selected) {
      return;
    }

    setLookupValue(selected.name || '');
    setNewInstitution((prev) => ({
      ...prev,
      name: selected.name || prev.name,
      description: selected.description || prev.description
    }));
  };

  const handleDeleteInstitution = async (institutionId) => {
    try {
      await deleteInstitution(institutionId);
      toast.current.show({ severity: 'warn', summary: 'Usunięto', detail: 'Instytucja został usunięty' });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się usunąć instytucję' });
    }
  };

  const handleRowEditComplete = async (e) => {
    const { newData } = e;
    try {
      await modifyInstitution(newData);
      toast.current.show({ severity: 'success', summary: 'Zapisano', detail: 'Instytucja zaktualizowany' });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się zaktualizować instytucję' });
    }
  };

  const textEditor = (options) => (
    <InputText type="text" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
  );

  const actionBodyTemplate = (rowData) => (
    <Button
      label="Usuń"
      icon="pi pi-trash"
      className="p-button-danger p-button-sm"
      onClick={() => handleDeleteInstitution(rowData._id)}
    />
  );

  const leftToolbarTemplate = () => (
    <Button
      label="Dodaj instytucję"
      icon="pi pi-plus"
      className="p-button-success"
      onClick={() => setShowDialog(true)}
    />
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <Card title="Zarządzanie instytucjami">
        <Toolbar className="mb-4" left={leftToolbarTemplate} />

        <div ref={tableWrapperRef}>
          <DataTable
            value={institutions}
            loading={isLoading || isFetching}
            paginator
            rows={visibleRows}
            editMode="row"
            dataKey="_id"
            onRowEditComplete={handleRowEditComplete}
          >
            <Column field="name" header="Nazwa" editor={textEditor} sortable></Column>
            <Column field="description" header="Opis" editor={textEditor} ></Column>
            <Column rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
            <Column body={actionBodyTemplate} header="Akcje"></Column>
          </DataTable>
        </div>
      </Card>

      <Dialog header="Dodaj instytucję" visible={showDialog} style={{ width: '30vw' }} onHide={() => setShowDialog(false)}>
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="institution-lookup">Szukaj instytucji (lokalnie po nazwie, REGON po NIP/REGON/KRS)</label>
            <AutoComplete
              id="institution-lookup"
              value={lookupValue}
              suggestions={institutionSuggestions}
              completeMethod={searchInstitutionSuggestions}
              field="name"
              itemTemplate={institutionSuggestionTemplate}
              onChange={(e) => setLookupValue(e.value)}
              onSelect={handleSuggestionSelect}
              placeholder="Wpisz nazwę lokalnie lub NIP/REGON/KRS do REGON"
            />
          </div>
          <div className="field">
            <label htmlFor="username">Nazwa</label>
            <InputText id="username" value={newInstitution.name} onChange={(e) => setNewInstitution({ ...newInstitution, name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="description">Opis</label>
            <InputText id="description" value={newInstitution.description} onChange={(e) => setNewInstitution({ ...newInstitution, description: e.target.value })} />
          </div> 
          <div className="flex justify-end gap-2 mt-4">
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label="Zapisz" icon="pi pi-check" onClick={handleAddInstitution} autoFocus />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
